import {useCallback, useRef} from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import {action, computed, makeAutoObservable} from "mobx";
import { useState } from "react";

import LinearProgressBar from "./LinearProgressBar";
import { MouseSquiggler } from "./MouseSquiggler";
import backgroundImage from "../assets/box-bg.png";
import {ENTROPY_FILL_RATE} from "../constants";
import {InfoBox} from "./InfoBox";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    margin: "10px 0px",
    justifyContent: "center",
  },
  paper: {
    width: "100%",
    textAlign: "center",
    background: `url(${backgroundImage}) no-repeat left top`,
    backgroundSize: "cover",
    borderRadius: "15px",
    zIndex: 1,
  },
  heading: {
    display: "flex",
    color: "#F782ED",
    fontWeight: 400,
    fontSize: 36,
    fontFamily: 'Gilroy-Medium, sans-serif',
    alignItems: 'center',
    justifyContent: "center",
    marginTop: "22px",
    marginBottom: "25px",
  },
  progressBar: {
    width: "90%",
    margin: "10px auto",
    marginBottom: "22px !important",
  },
  mouse: {
    width: "90%",
    margin: "5px auto",
    borderRadius: "15px",
  },
  squiggler: {
    position: "relative",
    background: "transparent",
  },
  mouseTitle: {
    fontWeight: 400,
    fontSize: "16px",
    color: "rgba(119, 130, 156, 1)",
    position: "absolute",
    bottom: "33px",
    textAlign: "center",
    width: "100%",
    padding: "0 40px",
    fontFamily: 'Gilroy-Regular, sans-serif',
  },
  buttonSectionReset: {
    padding: "0 4px 0px 0px",
  },
  buttonSectionSubmit: {
    padding: "0 0px 0px 4px",
  },
  button: {
    borderRadius: 10,
    fontWeight: 600,
    color: "white",
    fontFamily: 'Gilroy-Medium, sans-serif',
    fontSize: "16px",
  },
  buttonReset: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    padding: "13px 18px",
    '&:hover': {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    }
  },
  disabledResetButton: {
   color: "rgba(255, 255, 255, 0.41) !important",
   backgroundColor: "rgba(255, 255, 255, 0.1) !important",
  },
  submitEntropyBtn: {
    padding: "13px 18px",
    backgroundColor: "rgba(78, 55, 222, 1)",
    '&:hover': {
      backgroundColor: "rgba(78, 55, 222, 1)",
    }
  },
  disabledSubmitButton: {
    color: "rgba(255, 255, 255, 0.41) !important",
    backgroundColor: "rgba(78, 55, 222, 0.2) !important",
  },
  MainBtnSection: {
    marginBottom: "35px !important",
    marginTop: "20px"
  }
}));


export enum MouseAreaStage {
  DISABLED,
  ENABLED,
  SUBMITTED,
}

interface MouseAreaProps {
  stage: MouseAreaStage;
  submitEntropy: (squigglerEntropy: Number) => void;
  disabled: boolean;
}

interface ProgressBarProps {
  stage: MouseAreaStage;
  progressBarHolder: ProgressBarHolder;
}

const ProgressBarReactive = observer((props: ProgressBarProps) => {
  return (<LinearProgressBar progress={getProgress(props.stage, props.progressBarHolder.get())} />);
});

function getMouseAreaText(stage: MouseAreaStage): string {
  switch (stage) {
    case MouseAreaStage.DISABLED:
      return "Please connect your wallet to be able to generate entropy";
    case MouseAreaStage.ENABLED:
      return "Click and draw your mouse over this area to generate entropy";
    case MouseAreaStage.SUBMITTED:
      return "Thanks for submitting your entropy";
  }
}

function getProgress(stage: MouseAreaStage, globalCounter: number): number {
  switch (stage) {
    case MouseAreaStage.DISABLED:
      return 0;
    case MouseAreaStage.ENABLED:
      return +globalCounter.toFixed(2);
    case MouseAreaStage.SUBMITTED:
      return 100;
  }
}

// class to hold progress and trigger re-render of progress bar on increments
export class ProgressBarHolder {

  private value = 0;

  constructor() {
    makeAutoObservable(this);
  }

  @action
  increment(num: number) {
    if (this.value > 100.0) {
      this.value = 100.0;
      return false;
    }

    if (this.value === 100.0) {
      return false;
    }

    this.value += num;

    if (this.value > 100.0) {
      this.value = 100.0;
    }
  }

  @action
  reset() {
    this.value = 0;
  }

  @computed
  get() {
    return this.value;
  }

  @computed
  full() {
    return this.value >= 100.0;
  }
}

let squigglerEntropy: Number = Number(0);
let progress = new ProgressBarHolder();

const MouseArea = observer((props: MouseAreaProps) => {
  const classes = useStyles();
  const { stage, submitEntropy } = props;
  const disabled = stage !== MouseAreaStage.ENABLED || props.disabled;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rerenderCount, setRerenderCount] = useState<number>(0);

  const resetCanvas = () => {
    if (!canvasRef.current) {
        return;
    }

    const canvas: HTMLCanvasElement = canvasRef.current;

    let ctx = canvas.getContext("2d");

    if (ctx) {
      //Draw Canvas Fill mode
      ctx.fillStyle = `white`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }

  const newSquiggleNext = useCallback((newSquiggle: Number) => {
    squigglerEntropy = newSquiggle;
    if(!progress.increment(+ENTROPY_FILL_RATE)) {
      setRerenderCount(rerenderCount + 1);
    }
  }, [rerenderCount]);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={3}>
        <div className={classes.heading}>Entropy Generator
          <InfoBox
              title="Drawing in this box is an interactive way for each participant to provide the unique random data
              that is required to seed the art generating model."
          />
        </div>
        <div className={classes.progressBar}>
          <ProgressBarReactive stage={stage} progressBarHolder={progress} />
        </div>
        <div className={classes.mouse}>
          <Paper elevation={0} className={classes.squiggler}>
            <MouseSquiggler
              newSquiggleSelector={newSquiggleNext}
              disabled={disabled}
              canvasRef={canvasRef}
            />
            { progress.get() === 0 && (<div className={classes.mouseTitle}>{getMouseAreaText(stage)}</div>)}
          </Paper>
        </div>
        <Grid container spacing={0} className={classNames(classes.progressBar, classes.MainBtnSection)}>
          <Grid item xs={6} className={classes.buttonSectionReset}>
            <Button
              classes={{ disabled: classes.disabledResetButton }}
              className={classNames(classes.button, classes.buttonReset)}
              fullWidth
              variant="contained"
              disabled={disabled}
              onClick={() => {progress.reset(); resetCanvas(); }}
            >
              RESET
            </Button>
          </Grid>
          <Grid item xs={6} className={classes.buttonSectionSubmit}>
            <Button
              classes={{ disabled: classes.disabledSubmitButton }}
              className={classNames(classes.button, classes.submitEntropyBtn)}
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => submitEntropy(squigglerEntropy)}
              disabled={progress.get() < 100 || disabled}
            >
              SUBMIT ENTROPY
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
});

export default MouseArea;
